// iter150-review — Shared unread-count store. See useUnreadCount.js.
import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { refreshUnreadNow } from "@/lib/useUnreadCount";
import { toast } from "sonner";
import { Send, ChevronLeft, MessageSquare, RefreshCw, Plus, Megaphone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function Bubble({ m }) {
  const fromAdmin = m.fromRole === "admin";
  return (
    <div className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`} data-testid={`admin-inbox-msg-${m.id}`}>
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${fromAdmin ? "rounded-tr-sm bg-clay text-white" : "rounded-tl-sm border border-border bg-white text-ink-900"}`}>
        <div className={`mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ${fromAdmin ? "text-white/85" : "text-moss-dark"}`}>
          {fromAdmin ? "You (Admin)" : "School"} · {timeAgo(m.createdAt)}
        </div>
        <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>
      </div>
    </div>
  );
}

// Default Odia broadcast template — matches the SetupPriorityBanner / WhatsApp
// outreach so the same tone reaches schools that the admin hasn't messaged
// individually yet. Admin can edit before sending.
const DEFAULT_BROADCAST_SUBJECT = "ସ୍କୁଲ ସେଟଅପ୍ ଅଧୁରା — Setup Pending Reminder";
const DEFAULT_BROADCAST_BODY = [
  "⚠️ ସ୍କୁଲ୍ ସେଟଅପ୍ ଅଧୁରା ଅଛି!",
  "",
  "ନମସ୍କାର ଶିକ୍ଷକ ବନ୍ଧୁ,",
  "ଆପଣଙ୍କ ସ୍କୁଲ୍ ସେଟଅପ୍ ଏବେ ବି ବାକି ଅଛି। ସେଟଅପ୍ ସରିନଥିଲେ ଆପଣ ବିଲ୍ କିମ୍ବା ରିପୋର୍ଟ ବାହାର କରିପାରିବେ ନାହିଁ।",
  "",
  "ଏବେ ହିଁ ଏହି ଦୁଇଟି ମଧ୍ୟରୁ ଗୋଟିଏ ବାଛନ୍ତୁ:",
  "1️⃣ Online: ସେଟଅପ୍ ପୃଷ୍ଠାକୁ ଯାଇ ତଥ୍ୟ ଏଣ୍ଟ୍ରି ସାରନ୍ତୁ। (www.mdmsahayak.com)",
  "2️⃣ WhatsApp: ୱେବସାଇଟ୍‌ରୁ ଫର୍ମ ଡାଉନଲୋଡ୍ କରି ଫିଲପ୍ କରନ୍ତୁ ଓ ଫଟୋ ପଠାନ୍ତୁ। ଆମେ ଆପଣଙ୍କ ଆକାଉଣ୍ଟ Activate କରିଦେବୁ।",
  "",
  "ସମୟ ବଞ୍ଚାନ୍ତୁ, ଟେନସନ୍ ମୁକ୍ତ ହୁଅନ୍ତୁ!",
  "📞 WhatsApp: 8093414633",
  "ଟିମ୍ MDM ସହାୟକ",
].join("\n");

export default function AdminInbox() {
  const [threads, setThreads] = useState([]);
  const [schools, setSchools] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSchool, setComposeSchool] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  // Broadcast state
  const [bcOpen, setBcOpen] = useState(false);
  const [bcCap, setBcCap] = useState(100);
  const [bcSubject, setBcSubject] = useState(DEFAULT_BROADCAST_SUBJECT);
  const [bcBody, setBcBody] = useState(DEFAULT_BROADCAST_BODY);
  const [bcPreview, setBcPreview] = useState({ count: 0, schools: [] });
  const [bcLoading, setBcLoading] = useState(false);
  const [bcSending, setBcSending] = useState(false);
  const scrollRef = useRef(null);

  const refreshThreads = async () => {
    try {
      const r = await axios.get(`${API}/inbox/admin/threads`);
      setThreads(r.data || []);
      // iter150-review — keep the shared unread badge in sync.
      refreshUnreadNow();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load threads");
    }
  };

  const loadSchools = async () => {
    try {
      const r = await axios.get(`${API}/school-profiles`);
      setSchools(r.data || []);
    } catch { /* silent */ }
  };

  const openThread = async (id) => {
    setActiveId(id);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inbox/admin/threads/${id}`);
      setActive(r.data.thread);
      setMessages(r.data.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e7, behavior: "smooth" }), 60);
      refreshThreads();
      // iter150-review — admin opened the thread, server dropped
      // unreadAdmin on it; refresh the shared badge immediately.
      refreshUnreadNow();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open thread");
    } finally { setLoading(false); }
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/inbox/admin/reply`, { threadId: activeId, body });
      setReply("");
      await openThread(activeId);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  };

  const sendCompose = async () => {
    if (!composeSchool || !composeBody.trim()) {
      toast.error("Pick a school and write a message");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/inbox/admin/send`, {
        schoolId: composeSchool,
        subject: composeSubject || "Message from admin",
        body: composeBody,
      });
      toast.success("Message sent");
      setComposeOpen(false);
      setComposeBody(""); setComposeSubject(""); setComposeSchool("");
      await refreshThreads();
      await openThread(r.data.threadId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  useEffect(() => { refreshThreads(); loadSchools(); }, []);

  // Re-fetch broadcast preview whenever the user changes the cap slider.
  useEffect(() => {
    if (!bcOpen) return;
    let cancelled = false;
    setBcLoading(true);
    axios.get(`${API}/inbox/admin/broadcast-preview?maxPercent=${bcCap}`)
      .then((r) => { if (!cancelled) setBcPreview(r.data || { count: 0, schools: [] }); })
      .catch(() => { if (!cancelled) setBcPreview({ count: 0, schools: [] }); })
      .finally(() => { if (!cancelled) setBcLoading(false); });
    return () => { cancelled = true; };
  }, [bcOpen, bcCap]);

  const sendBroadcast = async () => {
    if (!bcBody.trim()) { toast.error("Write a message body first"); return; }
    if (!bcPreview.count) { toast.error("No schools match this filter"); return; }
    if (!window.confirm(`This will send the message to ${bcPreview.count} school${bcPreview.count === 1 ? "" : "s"} (setup < ${bcCap}%). Continue?`)) return;
    setBcSending(true);
    try {
      const r = await axios.post(`${API}/inbox/admin/broadcast`, {
        maxPercent: bcCap,
        subject: bcSubject,
        body: bcBody,
      });
      toast.success(`Sent to ${r.data?.sent || 0} school(s)`);
      setBcOpen(false);
      await refreshThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally { setBcSending(false); }
  };

  const totalUnread = useMemo(() => threads.reduce((s, t) => s + (t.unreadAdmin || 0), 0), [threads]);

  return (
    <section className="space-y-3" data-testid="admin-inbox-section">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold text-ink-900">Inbox</h2>
          <p className="text-xs text-ink-700">{threads.length} thread(s) · {totalUnread} unread reply(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={refreshThreads} data-testid="admin-inbox-refresh-btn" className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBcOpen(true)} data-testid="admin-inbox-broadcast-btn" className="gap-1.5 border-ochre/40 text-ochre-dark hover:bg-ochre/10">
            <Megaphone className="h-3.5 w-3.5" /> Broadcast
          </Button>
          <Button size="sm" onClick={() => setComposeOpen(true)} data-testid="admin-inbox-compose-btn" className="gap-1.5 bg-clay text-white hover:bg-clay-dark">
            <Plus className="h-3.5 w-3.5" /> New message
          </Button>
        </div>
      </header>

      {threads.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border bg-white p-10 text-center" data-testid="admin-inbox-empty">
          <MessageSquare className="mx-auto h-10 w-10 text-ink-500" />
          <p className="mt-3 text-sm font-semibold text-ink-900">No conversations yet</p>
          <p className="mt-1 text-xs text-ink-700">Click <b>New message</b> to start a thread with any school.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-[260px_1fr]">
          <aside className={`rounded-2xl border border-border bg-white p-2 ${activeId ? "hidden md:block" : ""}`} data-testid="admin-inbox-thread-list">
            {threads.map((t) => (
              <button type="button" key={t.id} onClick={() => openThread(t.id)}
                data-testid={`admin-inbox-thread-${t.id}`}
                className={`flex w-full items-start gap-2 rounded-xl px-3 py-2.5 text-left transition ${activeId === t.id ? "bg-clay/10" : "hover:bg-sand-50"}`}>
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-ink-900">{t.schoolName || "Unknown school"}</p>
                    {t.unreadAdmin > 0 && (
                      <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-clay px-1.5 text-[0.65rem] font-bold text-white">{t.unreadAdmin}</span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-[0.7rem] text-ink-700">{t.subject || "—"}</p>
                  <p className="mt-0.5 truncate text-[0.65rem] text-ink-700">{t.lastFromRole === "school" ? "👤 " : "🛡️ "}{t.lastBody || "—"}</p>
                </div>
              </button>
            ))}
          </aside>

          <section className={`flex min-h-[420px] flex-col rounded-2xl border border-border bg-white ${!activeId ? "hidden md:flex md:items-center md:justify-center" : ""}`}>
            {!active ? (
              <p className="text-sm text-ink-700">Pick a thread to read it.</p>
            ) : (
              <>
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                  <button className="grid h-8 w-8 place-items-center rounded-full hover:bg-sand-50 md:hidden" onClick={() => setActiveId(null)} data-testid="admin-inbox-back-btn">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-ink-900">{active.schoolName} <span className="text-[0.7rem] font-normal text-ink-700">· UDISE {active.udiseCode}</span></p>
                    <p className="truncate text-[0.7rem] text-ink-700">{active.subject}</p>
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-sand-50 px-4 py-4" data-testid="admin-inbox-thread-body" style={{ maxHeight: "60vh" }}>
                  {loading && <p className="text-xs text-ink-700">Loading…</p>}
                  {messages.map((m) => <Bubble key={m.id} m={m} />)}
                </div>
                <div className="flex items-end gap-2 border-t border-border bg-white px-3 py-3">
                  <textarea
                    data-testid="admin-inbox-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Reply to the school…"
                    rows={2}
                    className="block flex-1 resize-none rounded-xl border border-border bg-sand-50 px-3 py-2 text-sm outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
                  />
                  <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="admin-inbox-send-reply-btn" className="gap-1.5 bg-clay text-white hover:bg-clay-dark">
                    <Send className="h-4 w-4" /> Send
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent data-testid="admin-inbox-compose-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>New message to school</DialogTitle>
            <DialogDescription>Sends an inbox message to the selected school. They can reply from their /inbox page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">Send to school</span>
              <Select value={composeSchool} onValueChange={setComposeSchool}>
                <SelectTrigger data-testid="admin-inbox-compose-school" className="mt-1 h-10"><SelectValue placeholder="Pick a school" /></SelectTrigger>
                <SelectContent>
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`admin-inbox-compose-option-${s.udiseCode}`}>
                      {s.schoolName} · {s.udiseCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">Subject</span>
              <input data-testid="admin-inbox-compose-subject" value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="e.g. Setup pending" className="mt-1 block h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-clay focus:ring-1 focus:ring-clay/30" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">Message</span>
              <textarea data-testid="admin-inbox-compose-body" value={composeBody} onChange={(e) => setComposeBody(e.target.value)} rows={5} className="mt-1 block w-full resize-none rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-clay focus:ring-1 focus:ring-clay/30" />
            </label>
            <Button onClick={sendCompose} disabled={sending || !composeSchool || !composeBody.trim()} data-testid="admin-inbox-compose-send" className="w-full gap-1.5 bg-clay text-white hover:bg-clay-dark">
              <Send className="h-4 w-4" /> Send message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Broadcast dialog */}
      <Dialog open={bcOpen} onOpenChange={setBcOpen}>
        <DialogContent data-testid="admin-inbox-broadcast-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-ochre" /> Broadcast to pending schools</DialogTitle>
            <DialogDescription>
              Sends the same message into every school's inbox whose Setup completion is BELOW the chosen percentage.
              Tip: pick <b>100%</b> to message every school that hasn't finished setup yet.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Cap slider */}
            <label className="block">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-semibold text-ink-700">Send to schools with setup &lt; <span className="text-clay">{bcCap}%</span></span>
                <span className="rounded-full bg-clay/10 px-2 py-0.5 text-[0.65rem] font-semibold text-clay" data-testid="admin-inbox-broadcast-cap-label">{bcLoading ? "…" : `${bcPreview.count} school${bcPreview.count === 1 ? "" : "s"}`}</span>
              </div>
              <input
                data-testid="admin-inbox-broadcast-cap"
                type="range"
                min={1}
                max={100}
                step={1}
                value={bcCap}
                onChange={(e) => setBcCap(Number(e.target.value))}
                className="mt-2 w-full accent-clay"
              />
              <div className="mt-1 flex justify-between text-[0.6rem] text-ink-700"><span>1%</span><span>50%</span><span>100%</span></div>
            </label>

            {/* Subject + body */}
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">Subject</span>
              <input data-testid="admin-inbox-broadcast-subject" value={bcSubject} onChange={(e) => setBcSubject(e.target.value)} className="mt-1 block h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-clay focus:ring-1 focus:ring-clay/30" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">Message (Odia / English)</span>
              <textarea data-testid="admin-inbox-broadcast-body" value={bcBody} onChange={(e) => setBcBody(e.target.value)} rows={9} className="mt-1 block w-full resize-y rounded-lg border border-border bg-white px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-clay focus:ring-1 focus:ring-clay/30" />
            </label>

            {/* Recipient preview */}
            <div className="rounded-xl border border-border bg-sand-50 p-3" data-testid="admin-inbox-broadcast-preview">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700"><Users className="mr-1 inline h-3 w-3" /> Will be sent to ({bcPreview.count})</p>
              <div className="mt-2 max-h-44 overflow-y-auto space-y-1">
                {bcLoading && <p className="text-xs text-ink-700">Loading…</p>}
                {!bcLoading && bcPreview.count === 0 && (
                  <p className="text-xs italic text-ink-700">No school matches this filter.</p>
                )}
                {bcPreview.schools.slice(0, 50).map((s) => (
                  <div key={s.schoolId} className="flex items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5 text-xs">
                    <span className="truncate font-semibold text-ink-900">{s.schoolName}</span>
                    <span className="shrink-0 text-[0.65rem] text-ink-700">{s.udiseCode} · <b className={s.percent === 0 ? "text-rose-700" : "text-ochre-dark"}>{s.percent}%</b></span>
                  </div>
                ))}
                {bcPreview.count > 50 && (
                  <p className="px-1 pt-1 text-[0.65rem] italic text-ink-700">…and {bcPreview.count - 50} more.</p>
                )}
              </div>
            </div>

            <Button
              onClick={sendBroadcast}
              disabled={bcSending || bcLoading || bcPreview.count === 0 || !bcBody.trim()}
              data-testid="admin-inbox-broadcast-send"
              className="w-full gap-1.5 bg-ochre text-white hover:bg-ochre-dark disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {bcSending ? "Sending…" : `Send to ${bcPreview.count} school${bcPreview.count === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
